/**
 * Iter 299 — Step: Lieblings-Lebensmittel + Geschmacks-Profil + Religiös/Saison
 *
 * Ziel: KI lernt die Vorlieben des Kunden, baut sie aktiv in jeden
 * Tagesplan ein und vermeidet was nicht passt. Macht aus generischen
 * Plänen personalisierte Empfehlungen.
 */
import React, { useState } from 'react';
import { Heart, X, Plus, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

const COMMON_PREFER_FOODS = [
  'Lachs', 'Hähnchen', 'Avocado', 'Quinoa', 'Süßkartoffel',
  'Reis', 'Linsen', 'Brokkoli', 'Spinat', 'Joghurt',
  'Eier', 'Haferflocken', 'Beeren', 'Banane', 'Nüsse'
];

const TASTE_PROFILES = [
  { id: 'herzhaft', label: 'Herzhaft', desc: 'Wenig süß, viel Umami' },
  { id: 'suess', label: 'Süß', desc: 'Süße Snacks & Frühstück' },
  { id: 'scharf', label: 'Scharf', desc: 'Curry, Chili, Wasabi' },
  { id: 'mild', label: 'Mild', desc: 'Wenig Gewürze, klassisch' },
  { id: 'mediterran', label: 'Mediterran', desc: 'Olivenöl, Tomaten, Kräuter' },
  { id: 'asiatisch', label: 'Asiatisch', desc: 'Sojasauce, Ingwer, Reis' },
  { id: 'klassisch_ch', label: 'Schweizer Küche', desc: 'Rösti, Älplermagronen' },
];

const RELIGIOUS_OPTIONS = [
  { id: null, label: 'Keine' },
  { id: 'halal', label: 'Halal' },
  { id: 'kosher', label: 'Koscher' },
  { id: 'bio_preferred', label: 'Bio bevorzugt' },
];

const StepFoodPreferences = ({ prefs, setPrefs }) => {
  const [newFood, setNewFood] = useState('');

  const addPreferFood = (food) => {
    const f = (food || '').trim();
    if (!f) return;
    const current = prefs.prefer_foods || [];
    if (!current.includes(f)) {
      setPrefs({ ...prefs, prefer_foods: [...current, f] });
    }
    setNewFood('');
  };

  const removePreferFood = (food) => {
    setPrefs({ ...prefs, prefer_foods: (prefs.prefer_foods || []).filter(f => f !== food) });
  };

  const toggleTaste = (id) => {
    const current = prefs.taste_profile || [];
    const next = current.includes(id) ? current.filter(t => t !== id) : [...current, id];
    setPrefs({ ...prefs, taste_profile: next });
  };

  const tasteSelected = prefs.taste_profile || [];

  return (
    <div className="space-y-6" data-testid="onboarding-step-food-preferences">
      <div className="text-center mb-4">
        <Heart className="w-12 h-12 text-pink-500 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-white">Was magst du am liebsten?</h3>
        <p className="text-zinc-400 text-sm">
          Die KI baut diese Lebensmittel & Geschmäcker in deine Vorschläge ein
        </p>
      </div>

      {/* Lieblings-Lebensmittel */}
      <div>
        <Label className="text-white mb-2 block text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-pink-400" />
          Lieblings-Lebensmittel
        </Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {(prefs.prefer_foods || []).map(food => (
            <Badge
              key={food}
              data-testid={`prefer-food-${food}`}
              variant="default"
              className="gap-1 bg-pink-500/20 text-pink-300 border border-pink-500/40 hover:bg-pink-500/30"
            >
              {food}
              <X className="w-3 h-3 cursor-pointer" onClick={() => removePreferFood(food)} />
            </Badge>
          ))}
        </div>
        <div className="flex flex-wrap gap-1 mb-2">
          {COMMON_PREFER_FOODS.filter(f => !(prefs.prefer_foods || []).includes(f)).map(food => (
            <Badge
              key={food}
              data-testid={`add-prefer-${food}`}
              variant="outline"
              className="cursor-pointer hover:bg-pink-500/20 border-zinc-600 text-zinc-300 text-xs"
              onClick={() => addPreferFood(food)}
            >
              + {food}
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newFood}
            onChange={(e) => setNewFood(e.target.value)}
            placeholder="Eigenes Lieblings-Food..."
            onKeyPress={(e) => e.key === 'Enter' && addPreferFood(newFood)}
            className="bg-zinc-800 border-zinc-700 text-white text-sm"
            data-testid="prefer-food-input"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => addPreferFood(newFood)}
            className="border-zinc-600"
            data-testid="prefer-food-add-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Geschmacks-Profil */}
      <div>
        <Label className="text-white mb-2 block text-sm">Geschmacks-Profil (mehrere möglich)</Label>
        <div className="grid grid-cols-2 gap-2">
          {TASTE_PROFILES.map(t => (
            <button
              key={t.id}
              data-testid={`taste-${t.id}`}
              onClick={() => toggleTaste(t.id)}
              className={`p-3 rounded-lg border-2 text-left transition-all ${
                tasteSelected.includes(t.id)
                  ? 'border-pink-500 bg-pink-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <p className="text-sm font-medium text-white">{t.label}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Religiös/Ethisch */}
      <div>
        <Label className="text-white mb-2 block text-sm">Religiös / Ethisch (optional)</Label>
        <div className="grid grid-cols-2 gap-2">
          {RELIGIOUS_OPTIONS.map(opt => (
            <button
              key={opt.id || 'none'}
              data-testid={`religious-${opt.id || 'none'}`}
              onClick={() => setPrefs({ ...prefs, religious_diet: opt.id })}
              className={`p-3 rounded-lg border-2 transition-all ${
                (prefs.religious_diet || null) === opt.id
                  ? 'border-emerald-500 bg-emerald-500/10'
                  : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
              }`}
            >
              <p className="text-sm text-white">{opt.label}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Saisonal */}
      <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-zinc-700 bg-zinc-800/50">
        <div>
          <p className="text-sm font-medium text-white">Saisonal & regional bevorzugen</p>
          <p className="text-xs text-zinc-400 mt-0.5">
            KI nutzt im Winter Kürbis & Wurzelgemüse statt Wassermelone — frischer, günstiger, klimafreundlicher
          </p>
        </div>
        <Switch
          data-testid="seasonal-toggle"
          checked={prefs.seasonal_preference !== false}
          onCheckedChange={(checked) => setPrefs({ ...prefs, seasonal_preference: !!checked })}
        />
      </div>
    </div>
  );
};

export { COMMON_PREFER_FOODS, TASTE_PROFILES, RELIGIOUS_OPTIONS };
export default StepFoodPreferences;
